import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, AlertCircle, Clock, CheckCircle, XCircle,
  Tag, Calendar, Send, User, Shield,
} from 'lucide-react';
import { ticketService } from '../../services/ticketService';
import { useDateFormat } from '../../hooks/useDateFormat';

const STATUS_CONFIG = {
  OPEN: { label: 'Open', icon: AlertCircle, color: 'bg-blue-100 text-blue-700' },
  IN_PROGRESS: { label: 'In Progress', icon: Clock, color: 'bg-yellow-100 text-yellow-700' },
  RESOLVED: { label: 'Resolved', icon: CheckCircle, color: 'bg-green-100 text-green-700' },
  CLOSED: { label: 'Closed', icon: XCircle, color: 'bg-gray-100 text-gray-700' },
};

const PRIORITY_CONFIG = {
  HIGH: { label: 'High', color: 'bg-red-100 text-red-700' },
  MEDIUM: { label: 'Medium', color: 'bg-yellow-100 text-yellow-700' },
  LOW: { label: 'Low', color: 'bg-green-100 text-green-700' },
};

const TicketDetail = () => {
  const { t } = useTranslation();
  const { formatDateTime } = useDateFormat();
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const fetchTicket = async () => {
    try {
      const data = await ticketService.getTicketDetail(id);
      setTicket(data);
    } catch (err) {
      console.error('Failed to load ticket:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTicket();
  }, [id]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [ticket?.replies]);

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) return;

    setSending(true);
    try {
      await ticketService.addReply(id, replyMessage.trim());
      setReplyMessage('');
      fetchTicket();
    } catch (err) {
      console.error('Failed to send reply:', err);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-center py-20">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Ticket not found</h2>
        <button
          onClick={() => navigate('/portal/support')}
          className="text-primary-600 hover:underline"
        >
          {t('common.back')} to tickets
        </button>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.OPEN;
  const StatusIcon = statusConfig.icon;
  const priorityConfig = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.MEDIUM;
  const isClosed = ticket.status === 'CLOSED' || ticket.status === 'RESOLVED';

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={() => navigate('/portal/support')}
        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
        {t('common.back')} to Tickets
      </button>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content - Conversation */}
        <div className="lg:col-span-2 space-y-6">
          {/* Ticket Header */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <span className="text-sm text-gray-500 font-mono">{ticket.ticketNumber}</span>
                <h1 className="text-2xl font-bold text-gray-900 mt-1">{ticket.title}</h1>
              </div>
              <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${statusConfig.color}`}>
                <StatusIcon className="w-4 h-4" />
                {statusConfig.label}
              </span>
            </div>
            {ticket.description && (
              <p className="text-gray-700 whitespace-pre-wrap">{ticket.description}</p>
            )}
          </div>

          {/* Conversation Thread */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Conversation</h2>
            </div>

            <div className="p-4 space-y-4 max-h-[500px] overflow-y-auto">
              {(!ticket.replies || ticket.replies.length === 0) ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No replies yet. Our team will respond shortly.</p>
                </div>
              ) : (
                ticket.replies.map((reply) => (
                  <div
                    key={reply.id}
                    className={`flex ${reply.isAdminReply ? 'justify-start' : 'justify-end'}`}
                  >
                    <div className={`max-w-[80%] ${reply.isAdminReply ? 'order-2' : 'order-1'}`}>
                      <div className={`flex items-center gap-2 mb-1 ${reply.isAdminReply ? '' : 'justify-end'}`}>
                        {reply.isAdminReply ? (
                          <Shield className="w-4 h-4 text-primary-600" />
                        ) : (
                          <User className="w-4 h-4 text-gray-400" />
                        )}
                        <span className="text-xs font-medium text-gray-600">
                          {reply.repliedByName || reply.repliedByEmail || 'Unknown'}
                        </span>
                        <span className="text-xs text-gray-400">
                          {reply.createdAt ? formatDateTime(reply.createdAt) : ''}
                        </span>
                      </div>
                      <div
                        className={`rounded-xl px-4 py-3 ${
                          reply.isAdminReply
                            ? 'bg-primary-50 border border-primary-100 text-gray-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{reply.message}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply Input */}
            {!isClosed ? (
              <div className="p-4 border-t border-gray-200">
                <form onSubmit={handleSendReply} className="flex gap-3">
                  <input
                    type="text"
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder="Type your reply..."
                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <button
                    type="submit"
                    disabled={!replyMessage.trim() || sending}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                  >
                    <Send className="w-4 h-4" />
                    {sending ? t('common.loading') + '...' : t('common.submit')}
                  </button>
                </form>
              </div>
            ) : (
              <div className="p-4 border-t border-gray-200 text-center">
                <p className="text-sm text-gray-500">This ticket is {ticket.status.toLowerCase()}. No further replies can be added.</p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar - Ticket Details */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('common.details')}</h3>
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">{t('common.priority')}</p>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${priorityConfig.color}`}>
                  {priorityConfig.label}
                </span>
              </div>

              {ticket.serviceType && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Service</p>
                  <span className="text-sm font-medium text-gray-900 bg-gray-100 px-2.5 py-1 rounded">
                    {ticket.serviceType}
                  </span>
                </div>
              )}

              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Category</p>
                <span className="inline-flex items-center gap-1 text-sm text-gray-700">
                  <Tag className="w-4 h-4" />
                  {ticket.category ? ticket.category.replace('_', ' ') : 'Other'}
                </span>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Created By</p>
                <span className="text-sm text-gray-700">{ticket.createdByName || ticket.createdByEmail}</span>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">{t('common.created')}</p>
                <span className="inline-flex items-center gap-1 text-sm text-gray-700">
                  <Calendar className="w-4 h-4" />
                  {ticket.createdAt ? formatDateTime(ticket.createdAt) : ''}
                </span>
              </div>

              {ticket.updatedAt && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">{t('common.updated')}</p>
                  <span className="inline-flex items-center gap-1 text-sm text-gray-700">
                    <Calendar className="w-4 h-4" />
                    {formatDateTime(ticket.updatedAt)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketDetail;
